import React, { useMemo } from 'react';
import { formatRsvpDeadline, getEffectiveRsvpDeadline } from '../../../../utils/rsvpDeadline';

function parseIsoDateLocal(value) {
  if (typeof value !== 'string') return null;
  const match = value.trim().match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return null;
  const [, y, m, d] = match;
  const date = new Date(Number(y), Number(m) - 1, Number(d));
  return Number.isNaN(date.getTime()) ? null : date;
}

function getOccurrenceTime(form) {
  return form.time || form.recurrence?.time || null;
}

export function formatOccurrenceDateTime(form) {
  const occurrenceDate = parseIsoDateLocal(form?.occurrenceId);
  if (!occurrenceDate) return null;
  const dateText = occurrenceDate.toLocaleDateString('en-GB', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
  const time = getOccurrenceTime(form);
  return time ? `${dateText} at ${time}` : dateText;
}

function formatEventWhen(form) {
  if (form.type === 'oneOff') {
    if (!form.date || !form.time) return 'Date/time TBC';
    return `${new Date(form.date).toLocaleDateString()} at ${form.time}`;
  }
  const time = getOccurrenceTime(form);
  const interval = Number(form.recurrenceInterval || form.recurrence?.interval) || 1;
  if (form.frequency === 'WEEKLY') {
    return `${interval === 1 ? 'Every' : interval === 2 ? 'Fortnightly on' : `Every ${interval} weeks on`} ${form.dayOfWeek} at ${time || 'TBC'}`;
  }
  return `${form.weekOfMonth} ${form.dayOfWeek} every ${interval === 1 ? 'month' : `${interval} months`} at ${time || 'TBC'}`;
}

function formatLocation(form) {
  return [
    form.locationName,
    form.locationStreet,
    form.locationCity,
    form.locationPostcode,
    form.locationCountry,
  ]
    .filter((part) => typeof part === 'string' && part.trim() !== '')
    .join(', ');
}

export default function ReadOnlyEventOverview({ form }) {
  const prettyWhen = useMemo(() => formatEventWhen(form), [form]);
  const locationText = useMemo(() => formatLocation(form), [form]);
  const rsvpDeadlineText = useMemo(() => {
    const deadline = getEffectiveRsvpDeadline(form, {
      occurrenceId: form?.occurrenceId || '',
    });
    return formatRsvpDeadline(deadline);
  }, [form]);

  return (
    <div className="event-readonly-overview">
      <div className="event-readonly-overview__row">
        <strong>When:</strong> {prettyWhen}
      </div>
      <div className="event-readonly-overview__row">
        <strong>Duration:</strong>{' '}
        {parseInt(form.durationMinutes || 60, 10)} mins
      </div>

      {form.eventMode === 'online' ? (
        <div className="event-readonly-overview__row">
          <strong>Join URL:</strong>{' '}
          {form.onlineUrl ? (
            <a
              href={form.onlineUrl}
              target="_blank"
              rel="noreferrer"
            >
              {form.onlineUrl}
            </a>
          ) : (
            '—'
          )}
        </div>
      ) : (
        <div className="event-readonly-overview__row">
          <strong>Location:</strong> {locationText || '—'}
        </div>
      )}

      {form.tags?.length ? (
        <div className="event-readonly-overview__row">
          <strong>Tags:</strong> {form.tags.join(', ')}
        </div>
      ) : null}

      {rsvpDeadlineText ? (
        <div className="event-readonly-overview__row">
          <strong>RSVP:</strong> {rsvpDeadlineText.replace(/^RSVP deadline:\s*/, '')}
        </div>
      ) : null}
    </div>
  );
}
